import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {{ x: number, y: number } | null} */
  #swipeStart = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('touchstart', this.#handleSwipeStart, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#handleSwipeEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('touchstart', this.#handleSwipeStart);
    this.refs.dialog.removeEventListener('touchend', this.#handleSwipeEnd);
  }

  /** @param {TouchEvent} event */
  #handleSwipeStart = (event) => {
    const touch = event.touches[0];
    // A second finger means a pinch zoom, not a swipe
    this.#swipeStart = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /** @param {TouchEvent} event */
  #handleSwipeEnd = (event) => {
    const start = this.#swipeStart;
    const touch = event.changedTouches[0];
    this.#swipeStart = null;
    if (!start || !touch || event.touches.length > 0) return;

    // While zoomed in, a one-finger drag pans the image instead
    const wrapper = this.refs.dialog.querySelector('.zoom-dialog-image-visible drag-zoom-wrapper');
    const scale = wrapper instanceof HTMLElement ? parseFloat(wrapper.style.getPropertyValue('--drag-zoom-scale')) : 1;
    if (scale > 1.01) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

    if (deltaX < 0) this.goToNext();
    else this.goToPrev();
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;

    const imageItems = Array.from(media).filter(item =>
      item.classList.contains('zoom-dialog-image-item')
    );

    const targetImage = imageItems.find(item => {
      const originalIndex = item.getAttribute('data-original-index');
      return originalIndex !== null && parseInt(originalIndex) === index;
    });

    imageItems.forEach(item => {
      item.classList.remove('zoom-dialog-image-visible');
    });
    if (targetImage) {
      targetImage.classList.add('zoom-dialog-image-visible');
    }

    const open = () => {
      dialog.showModal();
      if (dialog.hasAttribute('scroll-lock')) {
        document.documentElement.setAttribute('scroll-lock', '');
      }
      if (targetImage) {
        targetImage.scrollIntoView({ behavior: 'instant' });
      }
      this.#updateArrowState();
      this.#updateDots();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const visibleImage = Array.from(media).find(item =>
      item.classList.contains('zoom-dialog-image-item') &&
      item.classList.contains('zoom-dialog-image-visible')
    );

    if (!visibleImage) return;

    const originalIndex = parseInt(visibleImage.getAttribute('data-original-index') || '0');
    this.#loadHighResolutionImage(visibleImage);
    this.dispatchEvent(new ZoomMediaSelectedEvent(originalIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the visible image (only one should be visible)
    const visibleImage = Array.from(media).find(item => 
      item.classList.contains('zoom-dialog-image-item') && 
      item.classList.contains('zoom-dialog-image-visible')
    );

    if (!visibleImage) return this.closeDialog();

    const originalIndex = parseInt(visibleImage.getAttribute('data-original-index') || '0');
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[originalIndex] : mediaGallery?.media?.[originalIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    visibleImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      visibleImage.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog, media } = this.refs;

    if (dialog.hasAttribute('scroll-lock')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    // Remove visible class from all images when closing
    const imageItems = Array.from(media).filter(item => 
      item.classList.contains('zoom-dialog-image-item')
    );
    imageItems.forEach(item => {
      item.classList.remove('zoom-dialog-image-visible');
    });
    
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToPrev();
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToNext();
      return;
    }
  }

  goToPrev() {
    this.#goToAdjacent(-1);
  }

  goToNext() {
    this.#goToAdjacent(1);
  }

  #goToAdjacent(direction) {
    const { media } = this.refs;
    const imageItems = Array.from(media).filter(item =>
      item.classList.contains('zoom-dialog-image-item')
    );
    if (imageItems.length <= 1) return;

    const currentIndex = imageItems.findIndex(item =>
      item.classList.contains('zoom-dialog-image-visible')
    );
    if (currentIndex === -1) return;

    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = imageItems.length - 1;
    if (newIndex >= imageItems.length) newIndex = 0;

    this.#showImage(newIndex);
  }

  /**
   * Shows the image at a position among the zoomable images (videos are skipped).
   * @param {number} position
   */
  goTo(position) {
    this.#showImage(Number(position));
  }

  /** @param {number} newIndex */
  #showImage(newIndex) {
    const imageItems = Array.from(this.refs.media).filter(item =>
      item.classList.contains('zoom-dialog-image-item')
    );
    const targetImage = imageItems[newIndex];
    if (!targetImage) return;

    imageItems.forEach(item => item.classList.remove('zoom-dialog-image-visible'));
    targetImage.classList.add('zoom-dialog-image-visible');

    const originalIndex = parseInt(targetImage.getAttribute('data-original-index') || '0');
    this.#loadHighResolutionImage(targetImage);
    this.dispatchEvent(new ZoomMediaSelectedEvent(originalIndex));
    this.#updateArrowState();
    this.#updateDots();
  }

  #updateDots() {
    const { media, dots } = this.refs;
    if (!dots) return;
    const imageItems = Array.from(media).filter(item =>
      item.classList.contains('zoom-dialog-image-item')
    );
    const currentIndex = imageItems.findIndex(item =>
      item.classList.contains('zoom-dialog-image-visible')
    );
    dots.forEach((dot, i) => dot.setAttribute('aria-selected', `${i === currentIndex}`));
  }

  #updateArrowState() {
    const { media, prevArrow, nextArrow } = this.refs;
    if (!prevArrow || !nextArrow) return;
    const imageItems = Array.from(media).filter(item =>
      item.classList.contains('zoom-dialog-image-item')
    );
    if (imageItems.length <= 1) {
      prevArrow.hidden = true;
      nextArrow.hidden = true;
      return;
    }
    const currentIndex = imageItems.findIndex(item =>
      item.classList.contains('zoom-dialog-image-visible')
    );
    prevArrow.hidden = false;
    nextArrow.hidden = false;
    prevArrow.disabled = currentIndex <= 0;
    nextArrow.disabled = currentIndex >= imageItems.length - 1;
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
